import Vue from "vue";

export function lazyLoad(componentLoader, props = {}) {
  return new Promise((resolve, reject) => {
    componentLoader()
      .then((Component) => {
        const component = Component.default || Component;
        if (typeof component === "function") {
          resolve(component(props));
        } else {
          resolve(component)
        }
      })
      .catch(reject);
  });
}

// Define the lazy loading component
export const lazyLoadComponentIfVisible = ({
  componentLoader,
  loadingComponent,
  errorComponent = null,
  props = {},
}) => {
  return Vue.extend({
    name: "LazyLoadedComponent",
    props: {
      componentLoader: Function, // Ensure componentLoader is a function
      props: Object, // Define a prop for props
    },
    data() {
      return {
        isVisible: false,
        component: null,
        error: null,
      };
    },
    computed: {
      isLoading() {
        return !this.component && !this.error;
      },
    },
    watch: {
      isVisible(newVal) {
        if (newVal && !this.component && !this.error) {
          this.loadComponent();
        }
      },
    },
    mounted() {
      if (!("IntersectionObserver" in window)) {
        this.loadComponent();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        this.isVisible = entries[0].isIntersecting;
      });

      if (this.observer) {
        this.observer.observe(this.$el);
      }
    },
    beforeUnmount() {
      if (this.observer) {
        this.observer.disconnect();
      }
    },
    methods: {
      async loadComponent() {
        try {
          const Component = await lazyLoad(componentLoader, this.props);
          this.component = Component;
        } catch (err) {
          this.error = err;
        } finally {
          if (this.observer) {
            this.observer.unobserve(this.$el);
          }
        }
      },
    },
    render(createElement) {
      if (this.isLoading) {
        return createElement(loadingComponent);
      } else if (this.error) {
        return createElement(errorComponent || "div", {
          domProps: { innerText: "Error loading component" },
        });
      } else {
        return createElement(this.component, {
          props: { ...this.props },
        });
      }
    },
  });
};